import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { getPostLoginRoute } from "./AuthProvider";
import {
  getStaffDashboardPath,
  type AppRole,
  type StaffSubRole,
} from "./roleRoutes";

type AllowedRole = Exclude<AppRole, "guest">;

type RoleProtectedRouteProps = {
  allowedRoles: AllowedRole[];
  allowedStaffSubRoles?: StaffSubRole[];
  children: React.ReactElement;
  redirectTo?: string;
};

function FullScreenLoader() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "#f7f8fa",
        color: "#374151",
        fontSize: "0.95rem",
      }}
    >
      Loading...
    </div>
  );
}

export default function RoleProtectedRoute({
  allowedRoles,
  allowedStaffSubRoles,
  children,
  redirectTo = "/login",
}: RoleProtectedRouteProps) {
  const { user, sessionLoading } = useAuth();
  const location = useLocation();

  if (sessionLoading) {
    return <FullScreenLoader />;
  }

  if (!user) {
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  const role = user.role;
  const staffSubRole = user.staffSubRole ?? null;

  if (!role || !allowedRoles.includes(role)) {
    return <Navigate to={getPostLoginRoute(user)} replace />;
  }

  if (
    role === "hnb_staff" &&
    allowedStaffSubRoles &&
    allowedStaffSubRoles.length > 0 &&
    (!staffSubRole || !allowedStaffSubRoles.includes(staffSubRole))
  ) {
    return <Navigate to={getStaffDashboardPath(staffSubRole)} replace />;
  }

  return children;
}